import React from 'react';
import Icon from '../../../components/AppIcon';

const ProvenanceTimeline = ({ 
  selectedCredit,
  onEventSelect,
  className = '' 
}) => {
  // Mock timeline data for mobile view
  const mockTimelineEvents = [
    {
      id: 'issuance-001',
      type: 'issuance',
      title: 'Credit Issuance',
      description: 'Initial credit issuance by project developer',
      timestamp: '2024-01-15T10:30:00Z',
      registry: 'Verra VCS',
      serialNumber: 'VCS-2024-001-12345',
      amount: 1000,
      status: 'verified',
      details: {
        projectName: 'Amazon Rainforest Conservation Project',
        methodology: 'VM0007 REDD+ Methodology Framework',
        vintageYear: '2023'
      }
    },
    {
      id: 'transfer-001',
      type: 'transfer',
      title: 'Registry Transfer',
      description: 'Transfer from project developer to carbon trader',
      timestamp: '2024-02-20T14:15:00Z',
      from: 'Project Developer ABC',
      to: 'Carbon Trader XYZ',
      amount: 1000,
      status: 'completed',
      details: {
        transferFee: '$250',
        transferType: 'Registry Internal',
        verificationRequired: true
      }
    },
    {
      id: 'split-001',
      type: 'split',
      title: 'Credit Split',
      description: 'Credits split into multiple batches for different buyers',
      timestamp: '2024-03-10T09:30:00Z',
      originalAmount: 1000,
      splitAmounts: [500, 500],
      status: 'completed',
      details: {
        reason: 'Multiple buyer allocation',
        splitFee: '$100',
        newSerialNumbers: ['VCS-2024-001-12345-A', 'VCS-2024-001-12345-B']
      }
    },
    {
      id: 'tokenization-001',
      type: 'tokenization',
      title: 'Blockchain Tokenization',
      description: 'Credits tokenized on Ethereum blockchain',
      timestamp: '2024-03-10T09:45:00Z',
      platform: 'Ethereum',
      contractAddress: '0x742d35Cc6634C0532925a3b8D',
      amount: 500,
      status: 'active',
      details: {
        tokenStandard: 'ERC-1155',
        gasUsed: '0.025 ETH',
        tokenizationProvider: 'Toucan Protocol'
      }
    },
    {
      id: 'retirement-001',
      type: 'retirement',
      title: 'Corporate Retirement',
      description: 'Credits retired for corporate net-zero claim',
      timestamp: '2024-09-15T16:20:00Z',
      company: 'Global Carbon Corp',
      claimType: 'VCMI Contribution',
      amount: 500,
      status: 'retired',
      details: {
        retirementReason: 'Annual carbon neutrality claim',
        vcmiClaimCode: 'VCMI-2024-GCC-001',
        auditTrail: 'Third-party verified'
      }
    }
  ];

  const getEventIcon = (type) => {
    switch (type) {
      case 'issuance': return 'Plus';
      case 'transfer': return 'ArrowRight';
      case 'tokenization': return 'Link';
      case 'split': return 'GitBranch';
      case 'retirement': return 'Archive';
      default: return 'Circle';
    }
  };

  const getEventColor = (type) => {
    switch (type) {
      case 'issuance': return 'bg-success';
      case 'transfer': return 'bg-secondary';
      case 'tokenization': return 'bg-warning';
      case 'split': return 'bg-primary';
      case 'retirement': return 'bg-error';
      default: return 'bg-muted-foreground';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return 'text-success';
      case 'completed': return 'text-success';
      case 'active': return 'text-warning';
      case 'retired': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    return `${amount?.toLocaleString()} tCO2e`;
  };

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="Clock" size={20} />
          <h3 className="text-lg font-semibold text-foreground">Transaction Timeline</h3>
        </div>
        <span className="text-sm text-muted-foreground">
          {mockTimelineEvents?.length} events
        </span>
      </div>
      {/* Timeline */}
      <div className="p-4">
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border"></div>

          {/* Timeline Events */}
          <div className="space-y-6">
            {mockTimelineEvents?.map((event, index) => (
              <div key={event?.id} className="relative flex items-start space-x-4">
                {/* Event Icon */}
                <div className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-full ${getEventColor(event?.type)} text-white`}>
                  <Icon name={getEventIcon(event?.type)} size={20} />
                </div>

                {/* Event Content */}
                <div 
                  className="flex-1 bg-background border border-border rounded-lg p-4 cursor-pointer hover:bg-muted/50 transition-smooth"
                  onClick={() => onEventSelect(event)}
                >
                  {/* Event Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">{event?.title}</h4>
                      <p className="text-xs text-muted-foreground">{event?.description}</p>
                    </div>
                    <div className="text-right">
                      <div className={`text-xs font-medium capitalize ${getStatusColor(event?.status)}`}>
                        {event?.status}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatTimestamp(event?.timestamp)}
                      </div>
                    </div>
                  </div>

                  {/* Event Details */}
                  <div className="space-y-2">
                    {event?.amount && (
                      <div className="flex items-center space-x-2">
                        <Icon name="Hash" size={14} className="text-muted-foreground" />
                        <span className="text-xs text-foreground">Amount: {formatAmount(event?.amount)}</span>
                      </div>
                    )}

                    {event?.registry && (
                      <div className="flex items-center space-x-2">
                        <Icon name="Database" size={14} className="text-muted-foreground" />
                        <span className="text-xs text-foreground">Registry: {event?.registry}</span>
                      </div>
                    )}

                    {event?.serialNumber && (
                      <div className="flex items-center space-x-2">
                        <Icon name="Tag" size={14} className="text-muted-foreground" />
                        <span className="text-xs font-mono text-foreground">{event?.serialNumber}</span>
                      </div>
                    )}

                    {event?.from && event?.to && (
                      <div className="flex items-center space-x-2">
                        <Icon name="Users" size={14} className="text-muted-foreground" />
                        <span className="text-xs text-foreground">
                          {event?.from} → {event?.to}
                        </span>
                      </div>
                    )}

                    {event?.platform && (
                      <div className="flex items-center space-x-2">
                        <Icon name="Layers" size={14} className="text-muted-foreground" />
                        <span className="text-xs text-foreground">Platform: {event?.platform}</span>
                      </div>
                    )}

                    {event?.company && (
                      <div className="flex items-center space-x-2">
                        <Icon name="Building" size={14} className="text-muted-foreground" />
                        <span className="text-xs text-foreground">Company: {event?.company}</span>
                      </div>
                    )}

                    {event?.claimType && (
                      <div className="flex items-center space-x-2">
                        <Icon name="Award" size={14} className="text-muted-foreground" />
                        <span className="text-xs text-foreground">Claim: {event?.claimType}</span>
                      </div>
                    )}
                  </div>

                  {/* Expand Indicator */}
                  <div className="flex items-center justify-between mt-3 pt-2 border-t border-border">
                    <span className="text-xs text-muted-foreground">
                      Click to view detailed information
                    </span>
                    <Icon name="ChevronRight" size={14} className="text-muted-foreground" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Footer */}
      <div className="p-4 border-t border-border bg-muted/50">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Complete transaction history</span>
          <button className="flex items-center space-x-1 hover:text-foreground transition-smooth">
            <Icon name="Download" size={14} />
            <span>Export Timeline</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProvenanceTimeline;